"use client";

import { useCallback, useEffect, useState } from "react";

import {
    BEAUTY_CAMPAIGN_CODE,
    isCampaignOfferActive,
    type PublicCampaign,
} from "@/shared/campaign/beautyCampaign";
import {
    fetchPublicCampaignBrowser,
    fetchPublicCampaignByIndustryBrowser,
} from "@/shared/services/campaignApi";

type Options = {
    /** When false — no network (avoids /campaigns 404 spam on platform home). */
    enabled?: boolean;
    initial?: PublicCampaign | null;
};

/**
 * Live campaign for an industry preset (platform → landing).
 * Falls back to BEAUTY_100_2026 for beauty when industry lookup is empty.
 */
export function useIndustryCampaign(industryPreset?: string | null, options: Options = {}) {
    const { enabled = true, initial = null } = options;
    const [campaign, setCampaign] = useState<PublicCampaign | null>(initial);
    const [loading, setLoading] = useState(Boolean(enabled && !initial));

    const refresh = useCallback(async () => {
        if (!enabled) {
            setLoading(false);
            return null;
        }
        const industry = industryPreset?.trim().toUpperCase();
        let next: PublicCampaign | null = null;
        try {
            if (industry) {
                next = await fetchPublicCampaignByIndustryBrowser(industry);
            }
            if (!next || next.status === "DISABLED") {
                next = await fetchPublicCampaignBrowser(BEAUTY_CAMPAIGN_CODE);
            }
        } catch {
            next = null;
        }
        // Keep last good payload if refresh fails — avoids modal stuck on "Проверяем…"
        if (next && typeof next.remaining === "number") {
            setCampaign(next);
        }
        setLoading(false);
        return next;
    }, [enabled, industryPreset]);

    useEffect(() => {
        if (!enabled) {
            setCampaign(null);
            setLoading(false);
            return undefined;
        }
        void refresh();
        const id = window.setInterval(() => {
            void refresh();
        }, 20_000);
        return () => window.clearInterval(id);
    }, [enabled, refresh]);

    return { campaign, loading, refresh, offerActive: isCampaignOfferActive(campaign) };
}

/** Beauty-only call sites. Pass enabled:false on platform home. */
export function useBeautyCampaign(initial?: PublicCampaign | null, enabled = true) {
    return useIndustryCampaign("BEAUTY", { enabled, initial: initial ?? null });
}
